import { Button } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '~/components/core'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import {
  formatRequiredByDate,
  recommendedUpdateDismissedAtom,
} from '~/components/specialized/RequiredUpdate/updateRequirementState'
import { usePartnerDesktopAppName } from '~/hooks/useCurrentPartner'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace, DesktopAnalyticsEvents } from '~/types/Analytics'
import { isElectron } from '~/utils/common'
import { openInNewTab } from '~/utils/functions'

type BannerTone = 'brand' | 'warning'

interface WrapperProps {
  $show: boolean
  $tone: BannerTone
}

// Grows with its content so longer translations and 200% zoom wrap instead
// of clipping.
const Wrapper = styled.div<WrapperProps>`
  min-height: ${({ $show }) => ($show ? '56px' : '0')};
  height: ${({ $show }) => ($show ? 'auto' : '0')};
  padding: ${({ $show }) => ($show ? '8px 16px' : '0 16px')};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
  background-color: var(--chakra-colors-${({ $tone }) => $tone}-solid);
  align-items: center;
  overflow: hidden;
  transition:
    height 200ms ease,
    min-height 200ms ease,
    padding 200ms ease,
    background-color 200ms ease;
`

const TextWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 8px;
  align-items: center;
  min-width: 0;
`

const bannerButtonProps = {
  bg: 'bg.panel',
  color: 'fg.default',
  borderRadius: 'full',
  _hover: { bg: 'bg.muted' },
  size: 'sm',
} as const

/**
 * Below recommendedVersion: a banner that Later hides until the next launch.
 * Below minimumVersion during a call: a warning that cannot be dismissed,
 * because RequiredUpdateGate blocks the app as soon as the call list empties.
 */
const UpdateRequirementBanner = ({ onShowCallback }: BannerProps) => {
  const { t, i18n } = useTranslation()
  const appName = usePartnerDesktopAppName()
  const [isDismissed, setIsDismissed] = useAtom(recommendedUpdateDismissedAtom)
  const {
    updateRequirement,
    isUpdateDownloaded,
    isUpdateBlocked,
    isUpdateNotAvailable,
    isChecking,
    downloadPercent,
    updateBlockedDownloadUrl,
    updatePermissionHelpUrl,
    updateIgnoringInstallPermission,
    checkForUpdates,
    quitAndInstall,
  } = useElectronUpdaterContext()
  const announce = useAnnounce()
  const trackedShownRef = useRef(false)

  const level = updateRequirement?.level
  const isRecommended = level === 'recommended' && !isDismissed
  const isRequiredDuringCall = level === 'required'
  const show = isElectron && (isRecommended || isRequiredDuringCall)
  const tone: BannerTone = isRequiredDuringCall ? 'warning' : 'brand'

  useEffect(() => {
    onShowCallback(show)
  }, [show, onShowCallback])

  useEffect(() => {
    if (!isRecommended || !updateRequirement || trackedShownRef.current) {
      return
    }
    trackedShownRef.current = true
    observe.track(
      AnalyticsNamespace.DESKTOP,
      DesktopAnalyticsEvents.UpdateRecommendedShown,
      {
        current_version: updateRequirement.currentVersion,
        recommended_version: updateRequirement.recommendedVersion,
      },
    )
  }, [isRecommended, updateRequirement])

  const handleLater = () => {
    setIsDismissed(true)
    observe.track(
      AnalyticsNamespace.DESKTOP,
      DesktopAnalyticsEvents.UpdateRecommendedDismissed,
      {
        current_version: updateRequirement?.currentVersion,
        recommended_version: updateRequirement?.recommendedVersion,
      },
    )
  }

  const downloadUrl =
    updateRequirement?.downloadUrl ?? updateBlockedDownloadUrl ?? null
  const requiredByDate = updateRequirement?.requiredBy
    ? formatRequiredByDate(updateRequirement.requiredBy, i18n.language)
    : null

  const message = isRequiredDuringCall
    ? t('updateRequirement.requiredDuringCall', {
        appName,
        defaultValue:
          'This version of {{appName}} is no longer supported. When your call ends, you must update to continue.',
      })
    : requiredByDate
      ? t('updateRequirement.recommendedMessageWithDate', {
          appName,
          date: requiredByDate,
          defaultValue: 'Update {{appName}} by {{date}} to continue to use it.',
        })
      : t('updateRequirement.recommendedMessage', {
          appName,
          defaultValue:
            'A new version of {{appName}} is available. Update to get the latest fixes.',
        })

  // Polite, so the warning queues behind the call instead of interrupting it.
  useEffect(() => {
    if (show && isRequiredDuringCall) {
      announce(message)
    }
  }, [show, isRequiredDuringCall])

  const wasUpdateDownloadedRef = useRef(isUpdateDownloaded)
  useEffect(() => {
    const finishedDownloading =
      isUpdateDownloaded && !wasUpdateDownloadedRef.current
    wasUpdateDownloadedRef.current = isUpdateDownloaded
    if (finishedDownloading && show && !isRequiredDuringCall) {
      announce(t('updateRequirement.restartToUpdate', 'Restart to update'))
    }
  }, [isUpdateDownloaded])

  const renderAction = () => {
    if (isRequiredDuringCall) {
      return null
    }
    if (isUpdateDownloaded) {
      return (
        <Button
          marginLeft=".5rem"
          onClick={quitAndInstall}
          {...bannerButtonProps}
        >
          {t('updateRequirement.restartToUpdate', 'Restart to update')}
        </Button>
      )
    }
    // This banner covers UpdatePermissionBanner, and main skips the check on a
    // Mac the user cannot write to, so offer its actions here.
    if (updatePermissionHelpUrl) {
      return (
        <>
          <Button
            marginLeft=".5rem"
            onClick={updateIgnoringInstallPermission}
            {...bannerButtonProps}
          >
            {t('updatePermissionBanner.updateAnyway', 'Update anyway')}
          </Button>
          <Button
            marginLeft=".5rem"
            onClick={() => openInNewTab(updatePermissionHelpUrl)}
            {...bannerButtonProps}
          >
            {t('updatePermissionBanner.howToFix', 'How to fix')}
          </Button>
        </>
      )
    }
    if ((isUpdateBlocked || isUpdateNotAvailable) && downloadUrl) {
      return (
        <Button
          marginLeft=".5rem"
          onClick={() => openInNewTab(downloadUrl)}
          {...bannerButtonProps}
        >
          {t('updateRequirement.downloadInstaller', 'Download installer')}
        </Button>
      )
    }
    // aria-disabled rather than disabled: this reuses the focused Update
    // button, and disabling it would drop focus to the page. data-disabled
    // keeps Chakra's disabled look, hover included.
    if (isChecking) {
      return (
        <Button
          marginLeft=".5rem"
          aria-disabled="true"
          data-disabled=""
          {...bannerButtonProps}
        >
          {downloadPercent === null
            ? t('updateCheck.checking', 'Checking for updates...')
            : t('updateRequirement.downloading', {
                percent: downloadPercent.toFixed(0),
                defaultValue: 'Downloading {{percent}}%',
              })}
        </Button>
      )
    }
    return (
      <Button
        marginLeft=".5rem"
        onClick={checkForUpdates}
        {...bannerButtonProps}
      >
        {t('updateRequirement.update', 'Update')}
      </Button>
    )
  }

  return (
    <Wrapper $show={show} $tone={tone}>
      <TextWrapper>
        <Text color={`var(--chakra-colors-${tone}-contrast)`}>{message}</Text>
        {renderAction()}
      </TextWrapper>
      {!isRequiredDuringCall && (
        <Button
          marginLeft=".5rem"
          onClick={handleLater}
          variant="ghost"
          size="sm"
          color="brand.contrast"
        >
          {t('updateRequirement.later', 'Later')}
        </Button>
      )}
    </Wrapper>
  )
}

export default UpdateRequirementBanner
